import { dataGridPersistentStateProps } from "src/components/common/dataGrid/dataGridStateStorage";
import { SystemDiagram } from "src/api/generated/postgres/db/types/public_/tables/systemDiagram";
import { enumColumn, textColumn } from "src/components/common/dataGrid/columns";
import { getSystemDiagramDirectionLabels } from "src/api/generated/postgres/db/types/public_/enums/systemDiagramDirection";
import { Site } from "src/api/generated/erp/db/types/tables/site";
import i18n from "i18next";
import { SystemDiagramCrudApi } from "src/api/generated/systemDiagrams/management/systemDiagramCrudApi";
import { SystemDiagramForm } from "src/components/views/erp/common/systemDiagrams/SystemDiagramForm.tsx";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender";
import { SystemDiagramDataGridApi } from "src/api/generated/systemDiagrams/management/systemDiagramDataGridApi.ts";
import { CrudDataGrid } from "src/components/common/dataGrid/crud/CrudDataGrid.tsx";

export interface SystemDiagramsDataGridProps {
	setSelectedSystemDiagram: (systemDiagram: SystemDiagram | undefined) => void;
}

export const SystemDiagramsDataGrid = ({
	setSelectedSystemDiagram,
}: SystemDiagramsDataGridProps) => {
	return (
		<AsyncFetchRender
			fetch={SystemDiagramDataGridApi.getSystemDiagramsData}
			content={({ systemDiagrams, siteOptions }, refresh) => (
				<CrudDataGrid<SystemDiagram>
					rows={systemDiagrams}
					refreshData={refresh}
					getRowId={(row) => row.systemDiagramId}
					columns={[
						textColumn({
							field: "name",
							headerName: i18n.t("name"),
							width: 200,
						}),
						textColumn({
							field: "contextType",
							headerName: i18n.t("context"),
							valueGetter: ({ row }) => contextColumnValueGetter(row, siteOptions),
							width: 200,
						}),
						enumColumn({
							field: "diagramDirection",
							headerName: i18n.t("diagram_direction"),
							enumLabels: getSystemDiagramDirectionLabels(),
							width: 200,
						}),
					]}
					onRowSelectionChanged={(rows) => {
						setSelectedSystemDiagram(rows[0]);
					}}
					disableMultipleRowSelection={true}
					remove={{
						type: "enabled",
						action: async (params) =>
							await SystemDiagramCrudApi.delete_({ systemDiagrams: params.items }),
					}}
					form={{
						dialogTitle: i18n.t("system_diagram"),
						dialogSize: "sm",
						component: ({ row, onCompleted, onFormEdited }) => {
							return (
								<SystemDiagramForm
									systemDiagram={row}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
								/>
							);
						},
						addRowEnabled: true,
						editEnabled: true,
					}}
					{...dataGridPersistentStateProps("A200F7B1338FFBA2")}
				/>
			)}
		/>
	);
};

const contextColumnValueGetter = (systemDiagram: SystemDiagram, siteOptions: Site[]): string => {
	const contextType = systemDiagram.contextType;
	let site: Site | null = null;
	if (contextType === "MANUFACTURING" || contextType === "SALES") {
		const siteId = Number(systemDiagram.contextParam1);
		site = siteOptions.find((site) => site.siteId === siteId) ?? null;
	}

	switch (contextType) {
		case "MANUFACTURING":
			return `${site?.siteName} | ${i18n.t("manufacturing")}`;
		case "SALES":
			return `${site?.siteName} | ${i18n.t("sales")}`;
		case "SPC":
			return i18n.t("spc");
	}
};
